import React, { useEffect } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { isLocalStorageEnabled } from '../services/caching/index.tsx';

import { useExecuteMSRNudge } from '../services/msr/index.tsx';
import { useMsrAnalytics } from '../services/msr/analytics.tsx';
import { useJpdCrossJoinNudgeEligibility } from './eligibility/jpd-eligibility.tsx';
import {
	useConfluenceCrossJoinNudgeEligibility,
	useHasSeenConfLlm,
	useLlmConfluenceCrossJoinNudgeEligibility,
} from './eligibility/confluence-eligibility.tsx';
import { useJpdCrossJoinNudge } from './nudges/jpd-nudge.tsx';
import { useLoomCrossJoinNudge } from './nudges/loom-nudge.tsx';
import { useConfluenceCrossJoinNudge } from './nudges/confluence-nudge.tsx';
import { getHasBeenEnrolledInMsr, setEnrolledInMsr } from './eligibility/msr-eligibility.tsx';
import {
	useHasSeenNudgesBefore,
	useLoomCrossJoinNudgeEligibility,
} from './eligibility/loom-eligibility.tsx';

/**
 * CrossJoinNudges conditionally renders various prompts based on user and site context.
 * Rendering occurs in a side-effectful way as targeting and eligibility checks are made synchronously at runtime.
 */
export const CrossJoinNudges = (): React.JSX.Element => {
	return (
		<JSErrorBoundary id="JiraCrossJoinNudges" teamName="groot" packageName="jiraCrossJoinNudges">
			<CrossJoinNudgesWithoutErrorBoundary />
		</JSErrorBoundary>
	);
};

/**
 * useCrossJoinNudges
 * @param execNudge is a temporary prop used to safely feature-gate the gate groot-1154-cjn-global-home
 * @deprecated use CrossJoinNudges instead
 */
export const useCrossJoinNudges = (execNudge = true): void => {
	const getJpdCrossJoinNudgeEligibility = useJpdCrossJoinNudgeEligibility();
	const getLoomCrossJoinNudgeEligibility = useLoomCrossJoinNudgeEligibility();
	const getConfluenceCrossJoinNudgeEligibility = useConfluenceCrossJoinNudgeEligibility();
	const showJpdNudge = useJpdCrossJoinNudge();
	const showLoomNudge = useLoomCrossJoinNudge();
	const showConfluenceNudge = useConfluenceCrossJoinNudge();
	const getLlmConfEligibility = useLlmConfluenceCrossJoinNudgeEligibility();
	const getHasSeenConfLlm = useHasSeenConfLlm();
	const getHasSeenNudgesBefore = useHasSeenNudgesBefore();
	const { fireMsrFeatureExposedEventOnce } = useMsrAnalytics();

	const executeMSRNudge = useExecuteMSRNudge();

	useEffect(() => {
		async function executeNudges({
			allowLoom = true,
			allowLlm = true,
		}: {
			allowLoom: boolean;
			allowLlm: boolean;
		}) {
			if (allowLoom) {
				if (fg('enable_puma_155_cjn_jira_loom')) {
					const loomEligibilityResult = await getLoomCrossJoinNudgeEligibility();
					if (loomEligibilityResult.isEligible) {
						showLoomNudge();
						return;
					}
				}
			}

			const jpdEligibilityResult = await getJpdCrossJoinNudgeEligibility();
			if (jpdEligibilityResult.isEligible) {
				showJpdNudge();
				return;
			}

			const confluenceEligibilityResult = await getConfluenceCrossJoinNudgeEligibility();
			if (confluenceEligibilityResult.isEligible) {
				showConfluenceNudge(confluenceEligibilityResult.data, allowLlm);
			}
		}

		async function executeNudgesWithMsr() {
			if (expValEquals('twcg-437-cjn-msr-m1', 'cohort', 'variation', 'not-enrolled')) {
				await setEnrolledInMsr();
				fireMsrFeatureExposedEventOnce({
					cohort: expVal('twcg-437-cjn-msr-m1', 'cohort', 'not-enrolled'),
				});
				executeMSRNudge();
			} else if (expValEquals('twcg-437-cjn-msr-m1', 'cohort', 'control', 'not-enrolled')) {
				await setEnrolledInMsr();
				fireMsrFeatureExposedEventOnce({
					cohort: expVal('twcg-437-cjn-msr-m1', 'cohort', 'not-enrolled'),
				});
				executeNudges({ allowLoom: false, allowLlm: false });
			} else {
				executeNudges({ allowLoom: false, allowLlm: false });
			}
		}

		async function orchestrateNudges() {
			if (fg('puma-296-cjn-orchestrator')) {
				const hasLocalStorage = await isLocalStorageEnabled();
				if (!hasLocalStorage) {
					return;
				}

				const enrolledInMsr = await getHasBeenEnrolledInMsr();
				// User already belongs to MSR control/variation
				if (enrolledInMsr) {
					executeNudgesWithMsr();
					return;
				}

				const hasSeenLlm = await getHasSeenConfLlm();
				// User already has seen LLM variation
				if (hasSeenLlm) {
					executeNudges({ allowLoom: false, allowLlm: true });
					return;
				}
				// kill switch to prevent new users or LLM control users from being sent to LLM
				if (fg('puma-296-cjn-orchestrator-llm')) {
					const isLLMEligible = await getLlmConfEligibility();
					// User would belong to LLM control
					if (isLLMEligible.isEligible) {
						executeNudges({ allowLoom: false, allowLlm: true });
						return;
					}
				}

				// User has seen any other nudge so they should not be part of MSR
				// User might be part of Loom variation, so they should not be part of LLM
				const isLoomGroup = await getHasSeenNudgesBefore();
				if (isLoomGroup) {
					executeNudges({ allowLoom: true, allowLlm: false });
					return;
				}

				// User doesn't belong in any other experiment so they should be sent to MSR control/variation
				executeNudgesWithMsr();
			} else {
				// Default control for non-MSR experiments
				executeNudges({ allowLlm: true, allowLoom: false });
			}
		}

		if (execNudge) {
			orchestrateNudges();
		}
	}, [
		execNudge,
		getConfluenceCrossJoinNudgeEligibility,
		getHasSeenConfLlm,
		getHasSeenNudgesBefore,
		getJpdCrossJoinNudgeEligibility,
		getLlmConfEligibility,
		getLoomCrossJoinNudgeEligibility,
		showConfluenceNudge,
		showJpdNudge,
		showLoomNudge,
		executeMSRNudge,
		fireMsrFeatureExposedEventOnce,
	]);
};

const CrossJoinNudgesWithoutErrorBoundary = () => {
	useCrossJoinNudges(true);

	return null;
};

export default CrossJoinNudges;
